/**
 * CartContext Module
 * 
 * This module provides a context for managing the shopping cart functionality.
 * It handles adding, updating, removing items and persists the cart state in cookies.
 */
import React, { createContext, useState, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';

// Create a context for cart functionality
export const CartContext = createContext();

/**
 * Custom hook to use the cart context
 * @returns {Object} Cart context value
 * @throws {Error} If used outside of CartProvider
 */
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

/**
 * CartProvider component
 * Provides cart state and functions to children components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const CartProvider = ({ children }) => {
  // Initialize cart from cookie or empty array
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = Cookies.get('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false); // Controls cart sidebar visibility

  // Update cookie whenever cart changes
  useEffect(() => {
    Cookies.set('cart', JSON.stringify(cartItems), { expires: 7 }); // Expires in 7 days
  }, [cartItems]);

  /**
   * Adds a product to the cart or updates quantity if already exists
   * @param {Object} product - Product to add to cart
   * @param {number} quantity - Quantity to add
   */
  const addToCart = (product, quantity) => {
    setCartItems(items => {
      const existingItem = items.find(item => item.id === product.id);
      if (existingItem) {
        // If item exists, update quantity but don't exceed available stock
        const newQuantity = Math.min(existingItem.qty_instock, existingItem.quantity + quantity);
        return items.map(item => 
          item.id === product.id ? { ...item, quantity: newQuantity } : item
        );
      }
      // If item doesn't exist, add it with the specified quantity
      return [...items, { ...product, quantity }];
    });
  };

  /**
   * Updates the quantity of an item in the cart
   * @param {string|number} id - ID of the item to update
   * @param {number} change - Amount to change quantity by (positive or negative)
   */
  const updateQuantity = (id, change) => {
    setCartItems(items => 
      items.map(item => {
        if (item.id === id) {
          // Ensure quantity stays between 1 and available stock
          const newQuantity = Math.max(1, Math.min(item.qty_instock, item.quantity + change));
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  /**
   * Removes an item from the cart
   * @param {string|number} id - ID of the item to remove
   */
  const removeItem = (id) => {
    setCartItems(items => items.filter(item => item.id !== id));
  };

  /**
   * Clears all items from the cart and removes the cart cookie
   */
  const clearCart = () => {
    setCartItems([]);
    Cookies.remove('cart');
  };

  // Context value with cart state and functions
  return (
    <CartContext.Provider value={{ 
      cartItems, 
      setCartItems, 
      addToCart, 
      updateQuantity, 
      removeItem,
      clearCart,
      isCartOpen,
      setIsCartOpen 
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
